import React, { useState } from 'react';
import {
  X,
  MapPin,
  Phone,
  User,
  Package,
  Clock,
  DollarSign,
  FileText,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
} from 'lucide-react';

const DELIVERY_TIME_WINDOWS = {
  1: '1:00 PM - 4:00 PM',
  2: '2:00 PM - 5:00 PM',
  3: '3:00 PM - 6:00 PM',
  4: '4:00 PM - 7:00 PM',
  5: '5:00 PM - 8:00 PM',
  6: '6:00 PM - 9:00 PM',
};

function getDeliveryTimeWindow(slot) {
  return DELIVERY_TIME_WINDOWS[Number(slot)] || null;
}

function DeliveryDetailModal({ delivery, onClose }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const deliveryTimeWindow = getDeliveryTimeWindow(delivery.slot);

  function getStatusColor(status) {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'encamino':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'entregado':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'cancelado':
      case 'rechazado':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En Camino',
      entregado: 'Entregado',
      cancelado: 'Cancelado',
      rechazado: 'Rechazado',
    };
    return labels[status] || status;
  }

  const images = delivery.image_urls || [];
  const hasImages = images.length > 0;

  function nextImage() {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  }

  function prevImage() {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-3 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-extrabold text-[#B1336E] font-montserrat">
              Detalle de Entrega
            </h2>
            <p className="text-xs text-slate-600 font-montserrat mt-0.5">
              ID: {delivery.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Status Badge */}
          <div className="flex justify-center">
            <span
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 font-montserrat ${getStatusColor(
                delivery.status
              )}`}
            >
              {getStatusLabel(delivery.status)}
            </span>
          </div>

          {/* Images Carousel */}
          <div className="bg-slate-50 rounded-xl overflow-hidden">
            {hasImages ? (
              <div className="relative aspect-video bg-slate-200">
                <img
                  src={images[currentImageIndex]}
                  alt={`Delivery ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4 text-slate-700" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                    >
                      <ChevronRight className="w-4 h-4 text-slate-700" />
                    </button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                      {images.map((_, index) => (
                        <div
                          key={index}
                          className={`w-1.5 h-1.5 rounded-full transition-colors ${
                            index === currentImageIndex
                              ? 'bg-white'
                              : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="aspect-video flex flex-col items-center justify-center bg-slate-100 text-slate-400">
                <ImageIcon className="w-12 h-12 mb-2" />
                <p className="text-xs font-montserrat">Sin imágenes disponibles</p>
              </div>
            )}
          </div>

          {/* Delivery Details Grid */}
          <div className="grid grid-cols-1 gap-3">
            {/* Recipient Name */}
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <User className="w-3.5 h-3.5" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Destinatario
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {delivery.recipient_name || 'N/A'}
              </p>
            </div>

            {/* Phone */}
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <Phone className="w-3.5 h-3.5" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Teléfono
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {delivery.recipient_phone || 'N/A'}
              </p>
            </div>

            {/* Address */}
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Dirección
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {delivery.address || 'N/A'}
              </p>
              {delivery.district && (
                <p className="text-xs text-slate-600 font-montserrat mt-1">
                  {delivery.district}
                </p>
              )}
            </div>

            {/* Product */}
            {delivery.product && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Package className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Producto
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {delivery.product}
                </p>
              </div>
            )}

            {/* Sequence */}
            {delivery.sequence && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Secuencia
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  #{delivery.sequence}
                </p>
              </div>
            )}

            {/* Delivery Time Window */}
            {deliveryTimeWindow && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-700 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Ventana Horaria
                  </span>
                </div>
                <p className="text-sm font-semibold text-red-800 font-montserrat">
                  {deliveryTimeWindow}
                </p>
              </div>
            )}

            {/* Arrival Time */}
            {delivery.arrival_time && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Hora de Llegada
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {new Date(delivery.arrival_time).toLocaleString('es-PE', {
                    day: '2-digit',
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            )}

            {/* Charged */}
            {delivery.charged !== undefined && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Cobrado
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {delivery.charged ? 'Sí' : 'No'}
                </p>
              </div>
            )}

            {/* Driver Notes */}
            {delivery.notes_driver && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Notas del Conductor
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat break-words">
                  {delivery.notes_driver}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#B1336E] hover:bg-[#B1336E]/90 text-white text-sm font-semibold rounded-lg transition-colors font-montserrat"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeliveryDetailModal;
